/* eslint-disable sonarjs/cognitive-complexity */
import { canAssist, assistHandler } from '@kk-activity-factory/quest-and-assist-module/assist';
import { globalInstance } from '@kk-activity-factory/utils';
import { type ModuleLocationInfoType } from '@kk-activity-factory/utils/types/moduleLocationInfo';
import Bus, { busEvent } from '@kk-activity-factory/bus';
import { PriorityEnum } from './priority-enum';
import globalData from '@kk-activity-factory/global-data';
import { useThrowCoinDialog } from '@kk-activity-factory/topic-rank/src/hooks/useThrowCoinDialog';

interface ModuleList {
    styleType: string;
    displayModuleType: string;
    moduleLocationInfo: ModuleLocationInfoType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    styleDetail: any;
    tradingSourceIdentifier: string;
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    moduleConfig: any;
}

export class PriorityProcessing {
    priorityOrder: Array<PriorityEnum> = [];

    moduleCollection: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [PriorityEnum.VISIT_GUIDE_DIALOG]: Array<any>;
        [PriorityEnum.ASSIST_USER_DIALOG]: boolean;
        [PriorityEnum.TITLE_IMAGE]: boolean;
        [PriorityEnum.VISIT_PRIZE_DIALOG]: ModuleList | null;
        [PriorityEnum.SIGN_IN_DIALOG]: ModuleList | null;
        [PriorityEnum.REPEAT_TASK_AUTO_OBTAIN_PRIZE_DIALOG]: boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [PriorityEnum.THROW_COIN_NOTICE_DIALOG]: any;
    } = {
        [PriorityEnum.VISIT_GUIDE_DIALOG]: [],
        [PriorityEnum.ASSIST_USER_DIALOG]: false,
        [PriorityEnum.TITLE_IMAGE]: false,
        [PriorityEnum.VISIT_PRIZE_DIALOG]: null,
        [PriorityEnum.SIGN_IN_DIALOG]: null,
        [PriorityEnum.REPEAT_TASK_AUTO_OBTAIN_PRIZE_DIALOG]: false,
        [PriorityEnum.THROW_COIN_NOTICE_DIALOG]: null,
    };

    constructor(priorityOrder: Array<PriorityEnum>) {
        this.priorityOrder = priorityOrder;
    }

    start(list: Array<ModuleList>) {
        console.info('PriorityProcessing.start', list);

        // 检查是否有助力模块
        if (canAssist()) {
            this.moduleCollection[PriorityEnum.ASSIST_USER_DIALOG] = true;
        }

        list.forEach((item) => {
            // 检查是否有头图模块
            if (item.styleType === 'TITLE_IMAGE' && item.displayModuleType === 'STATIC_IMG') {
                this.moduleCollection[PriorityEnum.TITLE_IMAGE] = true;
                return; // 跳过后面的检查
            }

            // 检查是否有访问引导弹窗
            if (item.styleType === 'ACCESS_GUIDE' && item.displayModuleType === 'VISIT') {
                this.moduleCollection[PriorityEnum.VISIT_GUIDE_DIALOG].push({
                    styleDetail: item.styleDetail,
                    id: item.tradingSourceIdentifier,
                    moduleId: item.id,
                });
                return; // 跳过后面的检查
            }

            // 检查是否有访问奖励弹窗
            if (item.styleType === 'ACTIVITY_VISIT' && item.displayModuleType === 'VISIT') {
                this.moduleCollection[PriorityEnum.VISIT_PRIZE_DIALOG] = item;
                return; // 跳过后面的检查
            }

            // 检查是否有金币投掷通知弹窗
            if (item.styleType === 'TOPIC_RANK') {
                if (this.moduleCollection[PriorityEnum.THROW_COIN_NOTICE_DIALOG]) {
                    this.moduleCollection[PriorityEnum.THROW_COIN_NOTICE_DIALOG].push(item);
                } else {
                    this.moduleCollection[PriorityEnum.THROW_COIN_NOTICE_DIALOG] = [item];
                }
                return; // 跳过后面的检查
            }

            // 检查是否有签到弹窗
            if (
                item.styleType === 'ACTIVITY_SIGN_IN' &&
                item.displayModuleType === 'SIGN_IN' &&
                (item.moduleLocationInfo.locationType === 'VIEW_WINDOW' ||
                    item.moduleLocationInfo.locationType === 'PAGE') &&
                item.styleDetail.openOnDailyFirstVisit
            ) {
                this.moduleCollection[PriorityEnum.SIGN_IN_DIALOG] = item;
                return; // 跳过后面的检查
            }

            if (item.styleType === 'ACTIVITY_SIGN_IN' && item.displayModuleType === 'SIGN_IN') {
                this.moduleCollection[PriorityEnum.REPEAT_TASK_AUTO_OBTAIN_PRIZE_DIALOG] = true;
            }
        });

        this.executeQueue();
    }

    /**
     * 执行队列
     */
    async executeQueue() {
        console.info('executeQueue');
        for (const key of this.priorityOrder) {
            // 头图模块的处理
            // eslint-disable-next-line sonarjs/no-collapsible-if
            if (key === PriorityEnum.TITLE_IMAGE) {
                // eslint-disable-next-line sonarjs/no-collapsible-if
                if (
                    globalData.isKuaikan ||
                    globalData.isKuaiKanMiniprogram ||
                    globalData.innerPreview
                ) {
                    if (this.moduleCollection[key]) {
                        const mutationsKey = globalInstance.getMutationsKeys();
                        globalInstance
                            .getRootStore()
                            .commit(mutationsKey.SET_NEED_FIRST_SHOW_TITLE_IMG, true);
                        Bus.$emit(busEvent.SHOULD_FIRST_OPEN_TITLE_IMG);
                        await this.waitTitleImageModule();
                    }
                }
            }
            // 进行助力的处理
            if (key === PriorityEnum.ASSIST_USER_DIALOG) {
                console.info('优先级处理：进入助力流程处理');
                if (this.moduleCollection[key]) {
                    await assistHandler();
                    console.info('优先级处理：助力流程完毕');
                } else {
                    console.info('优先级处理：没有助力流程，跳过');
                }
            }

            // 进行访问引导的处理
            if (key === PriorityEnum.VISIT_GUIDE_DIALOG) {
                console.info('优先级处理：进入访问引导处理');
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let openFn: ((args: any) => Promise<void>) | null = null;

                // eslint-disable-next-line sonarjs/no-collapsible-if
                if (
                    globalData.isKuaikan ||
                    globalData.isKuaiKanMiniprogram ||
                    globalData.innerPreview
                ) {
                    if (this.moduleCollection[key].length > 0) {
                        const { accessGuideUserInfo } = await import(
                            '@kk-activity-factory/visit-guide/apis/accessGuideUserInfo'
                        );
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const idSet = new Set<string>();
                        this.moduleCollection[key].forEach((item) => idSet.add(item.moduleId));
                        const {
                            data: { visitInfos },
                        } = await accessGuideUserInfo(Array.from(idSet.values()));
                        const shouldShowList = visitInfos
                            .filter((item) =>
                                this.getFilterModule(item, this.moduleCollection[key])
                            )
                            .map((item) => item.moduleId);
                        for (const item of this.moduleCollection[key]) {
                            if (shouldShowList.includes(item.moduleId)) {
                                if (!openFn) {
                                    const open = await import(
                                        '@kk-activity-factory/visit-guide/open'
                                    );
                                    openFn = open.openVisitGuide;
                                }
                                await openFn(item);
                            }
                        }
                    }
                }

                console.info('优先级处理：访问引导弹窗完毕');
            }

            // 进行访问奖励模块的处理
            if (key === PriorityEnum.VISIT_PRIZE_DIALOG) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let openFn: ((args: any) => Promise<void>) | null = null;
                // eslint-disable-next-line sonarjs/no-collapsible-if
                if (globalData.isKuaikan || globalData.isKuaiKanMiniprogram) {
                    if (this.moduleCollection[key]) {
                        const open = await import(
                            '@kk-activity-factory/module-bundle/components/VisitPrize/open'
                        );
                        openFn = open.open;
                        await openFn(this.moduleCollection[key]);
                    }
                }
                console.info('优先级处理：访问奖励弹窗完毕');
            }

            // 进行金币投掷通知弹窗的处理
            if (key === PriorityEnum.THROW_COIN_NOTICE_DIALOG) {
                console.info('优先级处理：进入金币投掷通知弹窗处理');
                const list = this.moduleCollection[key];
                if (list && list.length) {
                    for (const data of list) {
                        if (data && data.moduleConfig?.baseModule?.peltCoinNotify) {
                            await useThrowCoinDialog(data);
                        }
                    }
                }
            }

            // 进行签到弹窗的处理
            if (key === PriorityEnum.SIGN_IN_DIALOG) {
                console.info('优先级处理：进入签到弹窗处理');
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let openFn: ((args: any) => Promise<void>) | null = null;
                if (globalData.isKuaikan || globalData.isKuaiKanMiniprogram) {
                    if (this.moduleCollection[key]) {
                        console.info('this.moduleCollection[key]', this.moduleCollection[key]);
                        const isCurrentDayFirstVisit =
                            globalInstance.getRootStore().state.isCurrentDayFirstVisit;
                        if (isCurrentDayFirstVisit) {
                            const open = await import(
                                '@kk-activity-factory/sign-in-module/src/openSignDialog'
                            );
                            openFn = open.openSignDialog;
                            await openFn({
                                value: {
                                    rowData: this.moduleCollection[key],
                                },
                            });
                        }
                    } else {
                        console.info('没有签到弹窗');
                    }
                }

                console.info('优先级处理：签到弹窗处理完毕完毕');
            }

            // 进行重复任务自动领取处理
            // eslint-disable-next-line sonarjs/no-collapsible-if
            if (key === PriorityEnum.SIGN_IN_DIALOG) {
                if (globalData.isKuaikan || globalData.isKuaiKanMiniprogram) {
                    const mutationsKey = globalInstance.getMutationsKeys();
                    globalInstance
                        .getRootStore()
                        .commit(mutationsKey.SET_ALLOW_REPEAT_TASK_AUTO_OBTAIN_PRIZE);
                    Bus.$emit(busEvent.CHECK_AUTO_OBTAIN_PRIZE_ONCE);
                }
            }
        }
    }

    waitTitleImageModule() {
        return new Promise<void>((resolve) => {
            const titleImgModuleProcessFinished =
                globalInstance.getRootStore().state.titleImgModuleProcessFinished;
            if (titleImgModuleProcessFinished) {
                resolve();
            } else {
                Bus.$once(busEvent.TITLE_IMG_MODULE_HAD_CLOSED, () => {
                    resolve();
                });
            }
        });
    }

    // conditions: 1 checkConditions : "1-试用中", '2-非试用中'
    // prizeObtainType: 0活动期间首次访问。1每日首次访问 hasTrialRole(用户是否是试用角色还是非试用角色)： true 试用。false: 非试用
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getFilterModule(item: any, _modules: any) {
        let bool = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        _modules.forEach((stem: any) => {
            //  如果模块ID相同做比较
            if (stem.moduleId === item.moduleId) {
                // 后台配置了弹框展示条件， 在conditions 有值的情况下才判断，
                // conditions有值并分两种情况
                // 1-checkConditions=1并且hasTrialRole=true 后台配置的状态为试用 并且用户角色也是试用身份
                // 2-checkConditions=2并且hasTrialRole=false 后台配置的状态为非试用 并且用户角色也是非试用身份
                if (
                    stem.styleDetail.conditions &&
                    ((stem.styleDetail.checkConditions === 1 && item.hasTrialRole) ||
                        (stem.styleDetail.checkConditions === 2 && !item.hasTrialRole))
                ) {
                    // prizeObtainType=0，只判断用户是否是第一次进入
                    if (stem.styleDetail.prizeObtainType === 0 && item.firstVisit) {
                        bool = true;
                    }
                    // prizeObtainType=1 判断用户lastVisitDate(上次访问时间) 是否和currentDate(服务器当前时间)相等，不相等说明今天还未访问
                    if (
                        stem.styleDetail.prizeObtainType === 1 &&
                        item.lastVisitDate !== item.currentDate
                    ) {
                        bool = true;
                    }
                    // prizeObtainType=2 判断展示频次每次访问都展示弹框
                    if (stem.styleDetail.prizeObtainType === 2) {
                        bool = true;
                    }
                } else {
                    // prizeObtainType=0，只判断用户是否是第一次进入
                    // prizeObtainType=1 判断用户lastVisitDate(上次访问时间) 是否和currentDate(服务器当前时间)相等，不相等说明今天还未访问
                    // prizeObtainType=2 判断展示频次每次访问都展示弹框
                    if (!stem.styleDetail.conditions) {
                        if (stem.styleDetail.prizeObtainType === 0 && item.firstVisit) {
                            bool = true;
                        } else if (
                            stem.styleDetail.prizeObtainType === 1 &&
                            item.lastVisitDate !== item.currentDate
                            // eslint-disable-next-line sonarjs/no-duplicated-branches
                        ) {
                            bool = true;
                            // eslint-disable-next-line sonarjs/no-duplicated-branches
                        } else if (stem.styleDetail.prizeObtainType === 2) {
                            bool = true;
                        }
                    }
                }
            }
        });
        return bool;
    }
}
